import React, {
  Fragment,
  useEffect,
  useState,
  useCallback,
} from "react";
import {
  Autocomplete,
  Box,
  Checkbox,
  FormControl,
  FormControlLabel,
  InputLabel,
  MenuItem,
  Select,
  Snackbar,
  Tooltip,
  TextField,
  TextFieldProps,
  SelectChangeEvent,
} from "@mui/material";
import Button from "@mui/material/Button";
import { useTheme } from "@mui/material/styles";
import managementService from "../../../services/AdminManagementService";
import {
  AuthenticationMethod,
  SiteIntegrationMethodEnum,
  SiteIntegrationTypeEnum,
  SiteProviderEnum
} from "@beable/roster-backend-client-v1";
import MuiAlert from '@mui/material/Alert';
import InfoIcon from "@mui/icons-material/Info";
import {
  ORAPIClientConfigDTO,
  SiteDTO,
  SiteAuditLog,
} from "@beable/roster-backend-client-v1";
import {
  CustomerActionType,
  useCustomerDispatch,
} from "../../../context/CustomerContext";
import { generate24Hours, generateWeekdays } from "./RosterSource.utils";
import { getStyles } from "../styles";
import { CustomerSetupStore } from "./RosterSource.store";
import moment from "moment";

const weekdays = generateWeekdays();
const hours = generate24Hours(15);

const providerInitialValue: ORAPIClientConfigDTO = {
  id: "",
  applicationId: "",
  accessToken: "",
  endpointUrl: "",
  siteId: 0,
  provider: "",
  scheduledDays: "",
  scheduledHour: "",
  disableSchedule: true,
};

const customerSetupStore = new CustomerSetupStore();

interface RosterSourceManagementProps {
  setORAPIProvider: (orapiProvider: ORAPIClientConfigDTO) => void;
  orapiProvider: ORAPIClientConfigDTO;
  setSite: (site: SiteDTO) => void;
  site: SiteDTO;
  showMessage: (msg: string, severity: 'error' | 'info' | 'success' | 'warning') => void;
  closeMessage: () => void;
  messageSettings: {
    open: boolean;
    message: string;
    severity: 'error' | 'info' | 'success' | 'warning';
  };
  initalIntegrationType: SiteIntegrationTypeEnum;
}

export const RosterSourceManagement = ({
  setORAPIProvider,
  orapiProvider,
  setSite,
  site,
  showMessage,
  closeMessage,
  messageSettings,
  initalIntegrationType,
}: RosterSourceManagementProps) => {
  const theme = useTheme();
  const styles = getStyles(theme);
  const [isLoading, setIsLoading] = useState(false);
  const [auditLog, setAuditLog] = useState<SiteAuditLog>();
  const customerDispatch: any = useCustomerDispatch();
  const [newOrapiProvider, setNewORAPIProvider] = useState(orapiProvider);
  const { scheduledDays, scheduledHour, disableSchedule } = newOrapiProvider;
  const getAuditLogData = useCallback(async () => {
    if (site && site.id) {
      const log = await managementService.getLastIntegrationTypeAuditLog(
        site.id
      );
      if (log) {
        setAuditLog(log);
      }
    }
  }, [site]);
  const providerOptionsForORApi = [
    { value: SiteProviderEnum.ClassLink, title: 'ClassLink' },
    { value: 'OneRosterV1.1_OAuth2', title: 'One Roster v1.1 - OAuth2' },
  ];
  const providerOptionsForCleverApi = [
    { value: SiteProviderEnum.Clever, title: 'Clever' }
  ];

  useEffect(() => {
    setNewORAPIProvider(orapiProvider);
  }, [orapiProvider]);

  useEffect(() => {
    if (site.integrationType === SiteIntegrationTypeEnum.CleverAPI && newOrapiProvider.endpointUrl !== (SiteProviderEnum.Clever as string)) {
      setNewORAPIProvider({
        ...newOrapiProvider,
        endpointUrl: SiteProviderEnum.Clever
      });
    }
  }, [site]);

  useEffect(() => {
    if (!site) return;
    getAuditLogData();
  }, [site, getAuditLogData]);

  useEffect(() => {
    if (newOrapiProvider.provider === 'OneRosterV1.1_OAuth2' && newOrapiProvider.authenticationMethod !== AuthenticationMethod.OAuth2) {
      setNewORAPIProvider({ ...newOrapiProvider, authenticationMethod: AuthenticationMethod.OAuth2 });
    } else if (newOrapiProvider.provider === SiteProviderEnum.ClassLink.toString() && newOrapiProvider.authenticationMethod) {
      delete newOrapiProvider.authenticationMethod;
      setNewORAPIProvider({ ...newOrapiProvider });
    }
    if (site.integrationType === SiteIntegrationTypeEnum.CleverAPI && providerOptionsForCleverApi.length === 1 && newOrapiProvider.provider !== SiteProviderEnum.Clever.toString()) {
      setNewORAPIProvider({ ...newOrapiProvider, provider: SiteProviderEnum.Clever });
    }
  }, [newOrapiProvider]);

  const handleWeekDaysChange = (
    _event: React.SyntheticEvent<Element, Event>,
    value: string[]
  ) => {
    let ordered: string[] = [];
    weekdays.forEach((weekday, i) => {
      if (value.includes(weekday)) {
        ordered[i] = weekday;
      }
    });
    ordered = ordered.filter((i) => i !== undefined);
    setNewORAPIProvider({
      ...newOrapiProvider,
      scheduledDays: ordered.join(","),
    });
  };

  /**
   * Selecting integration partner (OneRoster, Clever, etc.)
   */
  const handleIntegrationTypeChange = (event: any, resetToDefault = false) => {
    const integrationType = resetToDefault
      ? SiteIntegrationTypeEnum.OneRosterCSV
      : (event.target.value as SiteIntegrationTypeEnum);

    customerSetupStore.previousIntegrationType = integrationType;
    setSite({ ...site, integrationType: integrationType });

    if (integrationType === initalIntegrationType) {
      setNewORAPIProvider(orapiProvider);
      return;
    }
    setNewORAPIProvider({ ...providerInitialValue, siteId: site.id });
  };

  const resetToOneRosterCSV = async () => {
    const response = await managementService.changeIntegrationType(
      SiteIntegrationTypeEnum.OneRosterCSV,
      SiteIntegrationMethodEnum.Manual,
      site.id
    );
    if (response) {
      showMessage("Reset to OneRosterCSV", "success");
      customerDispatch({
        type: CustomerActionType.SiteChanged,
        site,
      });

      await getAuditLogData();
    } else if (response === null)
      showMessage("The type is the same as before", "warning");
    else showMessage("Could not reset to OneRosterCSV...", "error");
  };

  /**
   * Manual or automatic
   */
  const handleIntegrationMethodChange = (event: any) => {
    const integrationMethod = event.target.value as SiteIntegrationMethodEnum;
    setSite({ ...site, integrationMethod: integrationMethod });
  };

  const handleOneRosterAPITest = async (event: any) => {
    event.preventDefault();
    if (
      newOrapiProvider.provider &&
      newOrapiProvider.applicationId &&
      newOrapiProvider.accessToken
    ) {
      setIsLoading(true);
      const response = await managementService.testAPIAccess(
        newOrapiProvider.applicationId,
        newOrapiProvider.accessToken,
        newOrapiProvider.provider,
        newOrapiProvider.authenticationMethod,
        newOrapiProvider.authenticationEndpoint,
        newOrapiProvider.endpointUrl
      );

      if (response) showMessage("API connection test succeeded", "success");
      else showMessage("API connection test failed", "error");

      setIsLoading(false);
    }
  };

  const handleApiIntegrationSubmit = async (event: any) => {
    event.preventDefault();
    const confirmSave = window.confirm(
      "Are you sure you would like to save this record?\r\nSaving incorrect/untested data may lead to errors later on."
    );
    if (!confirmSave) {
      return;
    }

    if (site && site.integrationType === SiteIntegrationTypeEnum.CleverAPI) {
      newOrapiProvider.provider = SiteProviderEnum.Clever;
      newOrapiProvider.applicationId = SiteProviderEnum.Clever;
    }

    if (
      newOrapiProvider.provider &&
      newOrapiProvider.applicationId &&
      newOrapiProvider.accessToken &&
      newOrapiProvider.siteId
    ) {
      setIsLoading(true);
      let updateResp = true;
      if (site && site.integrationType && site.integrationMethod) {
        const resp = await managementService.changeIntegrationType(
          site.integrationType,
          site.integrationMethod,
          site.id
        );
        updateResp = resp !== false; // true = changed; null = same; false = error;
      }
      if (updateResp) {
        await getAuditLogData();

        const updateFlag = !!newOrapiProvider.id;

        const response = updateFlag
          ? await managementService.updateAPIAccessData(newOrapiProvider)
          : await managementService.saveAPIAccessData(newOrapiProvider);
        if (response) {
          showMessage("API access data saved", "success");
          setORAPIProvider({ ...response });
          customerDispatch({
            type: CustomerActionType.SiteChanged,
            site,
          });
        } else {
          showMessage("API access data save failed", "error");
        }
      }
      setIsLoading(false);
    }
  };

  const handleApiIntegrationDelete = async (event: any) => {
    event.preventDefault();
    const confirmDelete = window.confirm(
      "Are you sure you would like to delete this record?\r\nThis action cannot be undone."
    );

    if (confirmDelete) {
      const siteId = orapiProvider.siteId;
      if (siteId) {
        setIsLoading(true);
        const response = await managementService.deleteAPIAccessData(siteId);
        if (response) {
          showMessage("API access data deleted", "success");
          setORAPIProvider({ ...providerInitialValue, siteId: siteId });
          setNewORAPIProvider({ ...providerInitialValue, siteId: siteId });
          await resetToOneRosterCSV();
          handleIntegrationTypeChange(null, true);
        } else {
          showMessage("API access data deletion failed", "error");
        }
        setIsLoading(false);
      }
    }
  };

  const handleSiteSubmit = async () => {
    if (site && site.integrationType && site.integrationMethod) {
      const updateResp = await managementService.changeIntegrationType(
        site.integrationType,
        site.integrationMethod,
        site.id
      );
      if (updateResp) {
        showMessage("Site edited successfully", "success");
        customerDispatch({
          type: CustomerActionType.SiteChanged,
          site,
        });

        await getAuditLogData();
      } else if (updateResp === null)
        showMessage("The type is the same as before", "warning");
      else showMessage("Could not edit the site...", "error");
    }
  };

  const setOrapiProviderOptions =
    (name: keyof ORAPIClientConfigDTO) =>
      (
        e:
          | React.ChangeEvent<{
            name?: string | AuthenticationMethod | undefined;
            value: unknown;
          }>
          | React.ChangeEvent<HTMLInputElement>
          | SelectChangeEvent<string | AuthenticationMethod>
      ) => {
        setNewORAPIProvider({
          ...newOrapiProvider,
          [name]:
            "checked" in e.target && name === "disableSchedule"
              ? e.target["checked"]
              : e.target.value,
        });
      };

  const ORApiAuthenticationMethodSelector = (site.integrationType === SiteIntegrationTypeEnum.OneRosterAPI) && (
    <FormControl
      fullWidth
      variant="outlined"
      sx={styles.indentedFields}
    >
      <InputLabel id="authentication-method-input-label" required>
        Authentication Method
      </InputLabel>
      <Select
        variant="outlined"
        labelId="authentication-method-labelId"
        id="authentication-method-id"
        label="Authentication Method"
        value={newOrapiProvider.authenticationMethod}
        onChange={setOrapiProviderOptions("authenticationMethod")}
        name="providerSelect"
      >
        {/* <MenuItem value="OAuth1" role="option">
          {AuthenticationMethod.OAuth1}
        </MenuItem> */}
        <MenuItem value="OAuth2" role="option">
          {AuthenticationMethod.OAuth2}
        </MenuItem>
      </Select>
    </FormControl>
  );
  const ORApiAuthenticationEndpoint = (
    <TextField
      name="authenticationEndpoint"
      variant="outlined"
      defaultValue={newOrapiProvider.authenticationEndpoint}
      label="Authentication Endpoint"
      onBlur={setOrapiProviderOptions("authenticationEndpoint")}
      fullWidth
      sx={styles.indentedFields}
      required
      slotProps={{
        htmlInput: {
          "data-testid": "authenticationEndpoint",
        }
      }}
    />
  );

  const CommonFields = (
    <Fragment>
      <TextField
        name="applicationId"
        variant="outlined"
        defaultValue={newOrapiProvider.applicationId}
        label="Application Id"
        onBlur={setOrapiProviderOptions("applicationId")}
        fullWidth
        sx={styles.indentedFields}
        required
        slotProps={{
          htmlInput: {
            "data-testid": "applicationId",
          }
        }}
      />
      <TextField
        name="accessToken"
        variant="outlined"
        defaultValue={newOrapiProvider.accessToken}
        label="Access Token"
        onBlur={setOrapiProviderOptions("accessToken")}
        fullWidth
        sx={styles.indentedFields}
        required
        slotProps={{
          htmlInput: {
            "data-testid": "accessToken",
          }
        }}
      />
    </Fragment>
  );

  const ORApiForm = (
    <Box sx={{ width: "95%" }}>
      <FormControl
        fullWidth
        variant="outlined"
        sx={styles.indentedFields}
      >
        <InputLabel id="provider-label" required>
          Provider
        </InputLabel>
        <Select
          variant="outlined"
          labelId="provider-label"
          id="providerSelect"
          label="Provider"
          value={newOrapiProvider.provider}
          onChange={setOrapiProviderOptions("provider")}
          name="providerSelect"
        >
          {
            providerOptionsForORApi.map((item, index) => (
              <MenuItem value={item.value} role="option" key={`idx-${index}`}>
                {item.title}
              </MenuItem>
            ))
          }
        </Select>
      </FormControl>
      {CommonFields}
      {
        (newOrapiProvider.provider === 'OneRosterV1.1_OAuth2') &&
        <TextField
          name="endpointUrl"
          variant="outlined"
          defaultValue={newOrapiProvider.endpointUrl}
          label="Endpoint URL"
          onBlur={setOrapiProviderOptions("endpointUrl")}
          fullWidth
          sx={styles.indentedFields}
          slotProps={{
            htmlInput: {
              "data-testid": "endpointUrl",
            }
          }}
        />
      }
      {newOrapiProvider.provider === 'OneRosterV1.1_OAuth2' && ORApiAuthenticationEndpoint}
    </Box>
  );

  const CleverForm = (
    <Box sx={{ width: "95%" }}>
      <FormControl
        fullWidth
        variant="outlined"
        sx={styles.indentedFields}
      >
        <InputLabel id="provider-label" required>
          Provider
        </InputLabel>
        <Select
          variant="outlined"
          labelId="provider-label"
          id="providerSelect"
          label="Provider"
          value={newOrapiProvider.provider}
          onChange={setOrapiProviderOptions("provider")}
          name="providerSelect"
        >
          {
            providerOptionsForCleverApi.map((item, index) => (
              <MenuItem value={item.value} role="option" key={`idx-${index}`}>
                {item.title}
              </MenuItem>
            ))
          }
        </Select>
      </FormControl>
      {CommonFields}
    </Box>
  );
  const ScheduleContainer = (
    <Box sx={{ width: "95%" }}>
      <Box sx={styles.indentedFields}>
        <Autocomplete
          id="mutipleDaysCheckboxSelect"
          disabled={disableSchedule}
          options={weekdays}
          multiple
          onChange={handleWeekDaysChange}
          getOptionLabel={(option) => option}
          filterSelectedOptions
          defaultValue={
            newOrapiProvider.scheduledDays.length > 0
              ? newOrapiProvider.scheduledDays.split(",")
              : []
          }
          renderInput={(
            params: JSX.IntrinsicAttributes & TextFieldProps
          ) => (
            <TextField
              {...params}
              variant="outlined"
              label="Select Days"
              required={!disableSchedule}
              slotProps={{
                ...params.slotProps,

                htmlInput: {
                  ...(params.slotProps?.htmlInput || {}),
                  required: !disableSchedule && scheduledDays.length === 0,
                }
              }}
            />
          )}
        />
      </Box>
      <FormControl
        fullWidth
        variant="outlined"
        disabled={disableSchedule}
        sx={styles.indentedFields}
      >
        <InputLabel id="hourSelect-label" required={!disableSchedule}>
          Select Hour
        </InputLabel>
        <Select
          variant="outlined"
          labelId="hourSelect-label"
          label="Select Hour"
          id="hourSelect"
          name="hourSelect"
          value={scheduledHour}
          onChange={setOrapiProviderOptions("scheduledHour")}>
          {hours.map((h) => (
            <MenuItem key={h} value={h}>
              {h}
            </MenuItem>
          ))}
        </Select>
      </FormControl>
      <FormControlLabel
        control={
          <Checkbox
            name="disableSchedule"
            sx={styles.cbs}
            checked={disableSchedule}
            onChange={setOrapiProviderOptions("disableSchedule")}
          />
        }
        label="Disable Schedule"
      />
    </Box>
  );

  const ButtonsContainer = (
    <FormControl
      variant="standard"
      sx={[styles.dfe, styles.mt2]}>
      <Button
        variant="contained"
        color="primary"
        id="saveAPIIntegration"
        onClick={handleApiIntegrationSubmit}
        sx={styles.ml2}
        disabled={
          isLoading ||
          !newOrapiProvider.accessToken ||
          (scheduledDays.length === 0 &&
            scheduledHour.length !== 0 &&
            !disableSchedule) ||
          (scheduledDays.length !== 0 &&
            scheduledHour.length === 0 &&
            !disableSchedule)
        }
      >
        Save
      </Button>
      {site.integrationType === SiteIntegrationTypeEnum.OneRosterAPI && (
        <Button
          variant="contained"
          color="primary"
          id="testAPIAccessBtn"
          onClick={handleOneRosterAPITest}
          disabled={
            isLoading ||
            !newOrapiProvider.accessToken ||
            !newOrapiProvider.provider ||
            !newOrapiProvider.applicationId
          }
        >
          Test API Access
        </Button>
      )}
      <Button
        variant="contained"
        id="deleteAPIIntegration"
        color="secondary"
        onClick={handleApiIntegrationDelete}
        sx={styles.ml2}
        disabled={isLoading || !newOrapiProvider.id}
      >
        Delete Credentials
      </Button>
    </FormControl>
  );

  const ApiOptions = () => {
    return (
      <Fragment>
        {site.integrationType === SiteIntegrationTypeEnum.OneRosterAPI && ORApiForm}
        {site.integrationType === SiteIntegrationTypeEnum.CleverAPI && CleverForm}
        {ScheduleContainer}
        {ButtonsContainer}
      </Fragment>
    );
  };

  return <>
    <FormControl
      fullWidth
      variant="outlined"
      sx={styles.pFormControl}
    >
      <InputLabel
        id="rosterType-label"
        sx={{
          pointerEvents: "auto",
          display: "inline-flex",
          alignItems: "center",
        }}
      >
        Roster Type{" "}
        {auditLog && (
          <Tooltip
            title={
              "Changed" +
              (auditLog.oldIntegrationType
                ? ` from ${auditLog.oldIntegrationType}`
                : "") +
              ` to ${auditLog.newIntegrationType} on ${moment(
                auditLog.setAt
              ).format("DD-MMM-YYYY")}`
            }
          >
            <InfoIcon color="primary"></InfoIcon>
          </Tooltip>
        )}{" "}
      </InputLabel>
      <Select
        variant="standard"
        labelId="rosterType-label"
        id="rosterTypeSelect"
        value={site.integrationType}
        label="Roster Type"
        onChange={(event) => handleIntegrationTypeChange(event)}
        required>
        <MenuItem value={SiteIntegrationTypeEnum.OneRosterCSV}>
          One Roster CSV
        </MenuItem>
        <MenuItem value={SiteIntegrationTypeEnum.OneRosterAPI}>
          One Roster API
        </MenuItem>
        <MenuItem value={SiteIntegrationTypeEnum.CleverAPI}>
          Clever API
        </MenuItem>
      </Select>
    </FormControl>
    <FormControl
      fullWidth
      variant="outlined"
      sx={styles.pFormControl}
    >
      <InputLabel id="rosterMethod-label">Roster Method</InputLabel>
      <Select
        variant="standard"
        labelId="rosterMethod-label"
        value={site.integrationMethod}
        onChange={handleIntegrationMethodChange}
        name={`${site.id}`}
        id="rosterMethodSelect"
        label="Roster Method"
        required>
        <MenuItem value={SiteIntegrationMethodEnum.Manual} role="option">
          {SiteIntegrationMethodEnum.Manual}
        </MenuItem>
        <MenuItem value={SiteIntegrationMethodEnum.Automatic} role="option">
          {SiteIntegrationMethodEnum.Automatic}
        </MenuItem>
      </Select>
    </FormControl>
    {site.integrationType === SiteIntegrationTypeEnum.OneRosterCSV && (
      <Box sx={styles.rightAlign}>
        <Button
          variant="contained"
          color="primary"
          id='saveCSVIntegration'
          onClick={handleSiteSubmit}
        >
          Save
        </Button>
      </Box>
    )}
    {(site.integrationType === SiteIntegrationTypeEnum.OneRosterAPI ||
      site.integrationType === SiteIntegrationTypeEnum.CleverAPI) && (
        <ApiOptions />
      )}
    <Snackbar
      anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      open={messageSettings.open}
      autoHideDuration={5000}
      onClose={closeMessage}
    >
      <MuiAlert
        onClose={closeMessage}
        severity={messageSettings.severity}
        elevation={6}
        variant="filled"
      >
        {messageSettings.message}
      </MuiAlert>
    </Snackbar>
  </>;
};
